import { MagnifyingGlass } from '@creditornot/cb-icons';
import { color } from '@creditornot/cb-ingredients/design-tokens';
import { Input } from '@creditornot/cb-input';
import { Empty } from '@creditornot/cb-placeholders';
import React, { useMemo, useState } from 'react';
import { useIntl } from 'react-intl';
import styled from 'styled-components';

import { DiscountedItem } from 'modules/campaigns/hooks/useDiscountedItems';

import { CategoryAccordionItem } from './CategoryAccordionItem';
import { DiscountedItemRow } from './DiscountedItemRow';
import { findCategoryItemsBySearchTerm, filterItemsBySearchTerm, DiscountedItemCategoryWithMatches } from './utils';

type SelectedDiscountedItemsModalContentProps = {
  discountedItems: DiscountedItem[];
  selectedCategories: DiscountedItemCategoryWithMatches[];
  selectedOtherItemIds: string[];
  selectedCategoryIds: string[];
  totalSelectedItemsCount: number;
};

const Container = styled.div`
  min-height: 100%;
  display: flex;
  flex-direction: column;
`;

const SearchInputContainer = styled.div`
  padding: 16px;
  border-block-end: 1px solid ${color.border};
`;

const StyledEmpty = styled(Empty)`
  margin-block: 24px;
  flex-grow: 1;
`;

// Helper to handle recursion from child categories
const CategoryContent = ({
  category,
  discountedItems,
  selectedCategoryIds,
  isParentSelected,
  identationLevel,
  accordionsExpanded,
  searchText,
}: {
  category: DiscountedItemCategoryWithMatches;
  discountedItems: DiscountedItem[];
  selectedCategoryIds: string[];
  isParentSelected: boolean;
  identationLevel: number;
  accordionsExpanded: boolean;
  searchText: string;
}) => {
  const isCategorySelected = isParentSelected || selectedCategoryIds.includes(category.id);

  return (
    <CategoryAccordionItem
      id={`category-${category.id}`}
      categoryName={category.name}
      includeNewItems={isCategorySelected}
      identationLevel={identationLevel}
      expanded={accordionsExpanded}
    >
      {category.itemIds.map(itemId => {
        const item = discountedItems.find(item => item.id === itemId);
        if (!item) {
          return null;
        }
        return (
          <DiscountedItemRow
            key={itemId}
            item={item}
            selected
            identationLevel={identationLevel}
            searchText={searchText}
          />
        );
      })}
      {category.childCategories.map(childCategory => (
        <CategoryContent
          key={`child-${childCategory.id}`}
          category={childCategory}
          discountedItems={discountedItems}
          selectedCategoryIds={selectedCategoryIds}
          isParentSelected={isCategorySelected}
          identationLevel={identationLevel + 1}
          accordionsExpanded={accordionsExpanded}
          searchText={searchText}
        />
      ))}
    </CategoryAccordionItem>
  );
};

// If there are a lot of items, showing them all initially causes performance issues
const EXPAND_ITEM_COUNT_LIMIT = 200;

export const SelectedDiscountedItemsModalContent = ({
  discountedItems,
  selectedCategories,
  selectedOtherItemIds,
  selectedCategoryIds,
  totalSelectedItemsCount,
}: SelectedDiscountedItemsModalContentProps) => {
  const { formatMessage } = useIntl();
  const selectedOtherItems = useMemo(
    () =>
      selectedOtherItemIds
        .map(itemId => discountedItems.find(item => item.id === itemId))
        .filter(item => !!item) as DiscountedItem[],
    [discountedItems, selectedOtherItemIds],
  );

  const [searchValue, setSearchValue] = useState('');
  const accordionsExpanded = totalSelectedItemsCount < EXPAND_ITEM_COUNT_LIMIT || searchValue.length >= 3;
  const searchTerm = accordionsExpanded ? searchValue : '';
  const visibleCategories = useMemo(
    () => findCategoryItemsBySearchTerm(discountedItems, selectedCategories, searchTerm),
    [discountedItems, selectedCategories, searchTerm],
  );
  const otherVisibleItems = useMemo(
    () => filterItemsBySearchTerm(selectedOtherItems, searchTerm),
    [selectedOtherItems, searchTerm],
  );

  return (
    <Container>
      <SearchInputContainer>
        <Input
          icon={<MagnifyingGlass />}
          onClearClick={() => setSearchValue('')}
          onChange={e => setSearchValue(e.target.value)}
          value={searchValue}
          placeholder={formatMessage({ id: 'campaigns.form.item.search-for-item' })}
        />
      </SearchInputContainer>
      {visibleCategories.map(category => (
        <CategoryContent
          key={`category-${category.id}`}
          category={category}
          discountedItems={discountedItems}
          selectedCategoryIds={selectedCategoryIds}
          isParentSelected={false}
          identationLevel={0}
          accordionsExpanded={accordionsExpanded}
          searchText={searchTerm}
        />
      ))}

      {!!otherVisibleItems.length && selectedCategories.length ? (
        <CategoryAccordionItem
          id={'other-items'}
          categoryName={formatMessage({ id: 'campaigns.form.item.all-other-items' })}
          expanded={accordionsExpanded}
        >
          {otherVisibleItems.map(item => (
            <DiscountedItemRow key={item.id} item={item} selected={false} searchText={searchTerm} />
          ))}
        </CategoryAccordionItem>
      ) : (
        // Do not display an expandable accordion if there are not categories selected
        otherVisibleItems.map(item => (
          <DiscountedItemRow key={item.id} item={item} selected={false} searchText={searchTerm} />
        ))
      )}

      {!visibleCategories.length && !otherVisibleItems.length && (
        <StyledEmpty title={formatMessage({ id: 'campaigns.form.item.no-item' })} />
      )}
    </Container>
  );
};
